import { Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  defer,
  finalize,
  map,
  of,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import {
  CollectionIntegrationAuthorization,
  CollectionRevisionDto,
  CollectionWorkflowDto,
} from '../interfaces/collection-revision.interface';
import {
  CollectionRevisionLifecyclePreconditionConflict,
  CollectionRevisionService,
} from '../services/collection-revision.service';
import { CollectionRevisionTargetRouteIdentity } from './collection-revision-route';

export interface CollectionRevisionLifecycleResult {
  readonly workflow: CollectionWorkflowDto;
  readonly authorization: CollectionIntegrationAuthorization | null;
}

@Injectable()
export class CollectionRevisionLifecycleCoordinator {
  private readonly revisionService = inject(CollectionRevisionService);
  private readonly inFlightByRevision = new Map<
    string,
    Observable<CollectionRevisionLifecycleResult>
  >();

  complete(
    identity: CollectionRevisionTargetRouteIdentity,
    workflow: CollectionWorkflowDto,
    revisionEtag: string,
    integrationEnabled: boolean,
  ): Observable<CollectionRevisionLifecycleResult> {
    requireExactWorkflow(identity, workflow, revisionEtag);
    const key = `${identity.collectionId}\u0000${identity.revisionId}`;
    const existing = this.inFlightByRevision.get(key);
    if (existing) return existing;

    const shared: Observable<CollectionRevisionLifecycleResult> = defer(() =>
      this.completeUnshared(
        identity,
        workflow,
        revisionEtag,
        integrationEnabled,
      ),
    ).pipe(
      finalize(() => {
        if (this.inFlightByRevision.get(key) === shared) {
          this.inFlightByRevision.delete(key);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.inFlightByRevision.set(key, shared);
    return shared;
  }

  private completeUnshared(
    identity: CollectionRevisionTargetRouteIdentity,
    workflow: CollectionWorkflowDto,
    revisionEtag: string,
    integrationEnabled: boolean,
  ): Observable<CollectionRevisionLifecycleResult> {
    if (workflow.revision_status !== 'DRAFT' || hasIncompleteTarget(workflow)) {
      return this.observeTerminalIntegration(
        identity,
        workflow,
        integrationEnabled,
      );
    }

    return this.sealWithOneReconciliation(
      identity,
      workflow,
      revisionEtag,
    ).pipe(
      switchMap((canonicalWorkflow) =>
        this.observeTerminalIntegration(
          identity,
          canonicalWorkflow,
          integrationEnabled,
        ),
      ),
    );
  }

  private sealWithOneReconciliation(
    identity: CollectionRevisionTargetRouteIdentity,
    workflow: CollectionWorkflowDto,
    revisionEtag: string,
  ): Observable<CollectionWorkflowDto> {
    return this.seal(identity, workflow, revisionEtag).pipe(
      catchError((error: unknown) => {
        if (
          !(error instanceof CollectionRevisionLifecyclePreconditionConflict)
        ) {
          return throwError(() => error);
        }
        return this.revisionService
          .getExactWorkflow(identity.collectionId, identity.revisionId)
          .pipe(
            map((currentWorkflow) => {
              requireExactWorkflow(
                identity,
                currentWorkflow,
                currentWorkflow.etag,
              );
              return currentWorkflow;
            }),
            switchMap((currentWorkflow) =>
              currentWorkflow.revision_status === 'DRAFT' &&
              !hasIncompleteTarget(currentWorkflow)
                ? this.seal(identity, currentWorkflow, currentWorkflow.etag)
                : of(currentWorkflow),
            ),
          );
      }),
    );
  }

  private seal(
    identity: CollectionRevisionTargetRouteIdentity,
    workflow: CollectionWorkflowDto,
    revisionEtag: string,
  ): Observable<CollectionWorkflowDto> {
    return this.revisionService
      .sealRevision(identity.collectionId, identity.revisionId, revisionEtag)
      .pipe(
        map((sealed) =>
          projectWorkflowAfterRevision(
            workflow,
            sealed.revision,
            sealed.revisionEtag,
          ),
        ),
      );
  }

  private observeTerminalIntegration(
    identity: CollectionRevisionTargetRouteIdentity,
    workflow: CollectionWorkflowDto,
    integrationEnabled: boolean,
  ): Observable<CollectionRevisionLifecycleResult> {
    if (
      !integrationEnabled ||
      (workflow.revision_status !== 'SEALED' &&
        workflow.revision_status !== 'SUBMITTED') ||
      workflow.revision_kind !== 'INITIAL' ||
      !workflow.targets.some(
        (target) => target.mean_type_snapshot === 'CONNECTION',
      )
    ) {
      return of({ workflow, authorization: null });
    }

    return this.revisionService
      .authorizeIntegrationRevision(identity.collectionId, identity.revisionId)
      .pipe(map((authorization) => ({ workflow, authorization })));
  }
}

function requireExactWorkflow(
  identity: CollectionRevisionTargetRouteIdentity,
  workflow: CollectionWorkflowDto,
  revisionEtag: string,
): void {
  if (
    workflow.collection_id !== identity.collectionId ||
    workflow.revision_id !== identity.revisionId ||
    workflow.etag !== revisionEtag
  ) {
    throw new Error(
      'The collection revision lifecycle input does not identify one exact revision.',
    );
  }
}

function hasIncompleteTarget(workflow: CollectionWorkflowDto): boolean {
  return workflow.targets.some(
    (target) => target.target_status !== 'COMPLETED',
  );
}

function projectWorkflowAfterRevision(
  workflow: CollectionWorkflowDto,
  revision: CollectionRevisionDto,
  revisionEtag: string,
): CollectionWorkflowDto {
  if (
    revision.collection_id !== workflow.collection_id ||
    revision.id !== workflow.revision_id ||
    revision.kind !== workflow.revision_kind
  ) {
    throw new Error(
      'The lifecycle revision does not match the collection workflow.',
    );
  }
  const editable = revision.editable && revision.status === 'DRAFT';
  return Object.freeze({
    ...workflow,
    revision_no: revision.revision_no,
    revision_status: revision.status,
    editable,
    allowed_actions: Object.freeze([...revision.allowed_actions]),
    etag: revisionEtag,
    targets: Object.freeze(
      workflow.targets.map((target) => Object.freeze({ ...target })),
    ),
  });
}
